// The AppwriteRemote adapter. Everything it does is create or read — no
// update, no delete, on this port or on the server.

import { parseEvent, type ProgressEvent } from "@aybuben/core/progress/event.ts";
import type { GameState } from "@aybuben/core/state.ts";

import {
	codeOf,
	type PermissionApi,
	type QueryApi,
	type RoleApi,
	type RowsApi,
} from "../appwrite/sdk.ts";
import { coerceState } from "../progress/local.ts";
import { RemoteError, type EventPage, type RemoteProgress, type RemoteSnapshot } from "./remote.ts";

/** SDK exception -> our closed union. */
// Exported only for appwrite.test.ts, the same reason parseSnapshotState is.
export const kind = (e: unknown): RemoteError => {
	const code = codeOf(e);
	if (!code) return new RemoteError("offline");
	if (code === 400) return new RemoteError("cursor");
	if (code >= 500) return new RemoteError("server");
	// A dead session is not a transport problem and must not be retried: the
	// engine would hit the API every 30-60s indefinitely, and the 429s that
	// manufactures are what used to sign learners out.
	if (code === 401 || code === 403) return new RemoteError("unauthorized", `appwrite ${code}`);
	return new RemoteError("unknown", `appwrite ${code}`);
};

/** What goes in the `payload` column: everything the columns do not already hold. */
const payloadOf = (e: ProgressEvent): string => {
	const { id, at, dev, kind: _k, ...rest } = e;
	return JSON.stringify(rest);
};

/**
 * A raw `state` column decoded into a usable GameState, or null for anything
 * this build cannot parse or make sense of.
 *
 * Every snapshot is a cache, so a `state` column this build cannot
 * read is a cache miss, not an error — the caller re-folds from the event
 * log instead. Exported (the same reason auth/appwrite.ts exports authCode)
 * so this actual decision is testable without a live SDK call. Shares
 * coerceState with progress/local.ts's loadLocal(), which faces the exact
 * same trust boundary against a locally stored snapshot, so the two do not
 * drift into two different notions of "a usable GameState".
 */
export function parseSnapshotState(raw: string): GameState | null {
	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return null;
	}
	return coerceState(parsed);
}

export interface RemoteSdk {
	tables: RowsApi;
	Query: QueryApi;
	Permission: PermissionApi;
	Role: RoleApi;
	databaseId: string;
	eventsTable: string;
	snapshotsTable: string;
}

export class AppwriteRemote implements RemoteProgress {
	constructor(private readonly sdk: RemoteSdk) {}

	async pullSnapshot(userId: string): Promise<RemoteSnapshot | null> {
		try {
			const row = await this.sdk.tables.getRow({
				databaseId: this.sdk.databaseId,
				tableId: this.sdk.snapshotsTable,
				rowId: userId,
			});
			// An unguarded `as GameState` here used to let a SyntaxError from a
			// malformed `state` column escape into the outer catch, where kind()
			// would misclassify a data problem as "offline" — the engine would
			// then retry forever against a row that will never parse.
			const state = parseSnapshotState(String(row.state));
			if (!state) return null;
			return {
				version: Number(row.version),
				content: String(row.content),
				coversUpTo:
					row.coversUpTo === null || row.coversUpTo === undefined ? null : String(row.coversUpTo),
				state,
			};
		} catch (e) {
			if (codeOf(e) === 404) return null;
			throw kind(e);
		}
	}

	async pullEvents(userId: string, after: string | null, limit: number): Promise<EventPage> {
		try {
			const queries = [
				this.sdk.Query.equal("userId", userId),
				// Server time, not the client stamp: the two clocks are separate. Coverage uses
				// the server's clock because it is monotonic regardless of client
				// skew; ordering uses the hybrid logical clock, in fold.ts.
				this.sdk.Query.orderAsc("$createdAt"),
				this.sdk.Query.limit(limit),
				...(after ? [this.sdk.Query.cursorAfter(after)] : []),
			];
			const res = await this.sdk.tables.listRows({
				databaseId: this.sdk.databaseId,
				tableId: this.sdk.eventsTable,
				queries,
			});
			const events: ProgressEvent[] = [];
			for (const row of res.rows) {
				let body: unknown;
				try {
					body = JSON.parse(String(row.payload));
				} catch {
					body = null;
				}
				// Trust boundary: `body` is unknown JSON this or another build wrote
				// into the payload column; spreading it into the candidate is safe
				// because parseEvent re-validates every field before returning one.
				const candidate = {
					id: row.$id,
					at: row.at,
					dev: row.dev,
					kind: row.kind,
					...(body as Record<string, unknown> | null),
				};
				const one = parseEvent(candidate);
				// A row this build cannot interpret is skipped, never guessed at.
				if (one) events.push(one);
			}
			const last = res.rows.at(-1);
			return { events, cursor: last ? last.$id : after, more: res.rows.length === limit };
		} catch (e) {
			throw kind(e);
		}
	}

	async push(userId: string, e: ProgressEvent): Promise<void> {
		try {
			await this.sdk.tables.createRow({
				databaseId: this.sdk.databaseId,
				tableId: this.sdk.eventsTable,
				// The row id IS the event id. That single choice is what makes a
				// retried push idempotent, and it is why there is no client-side
				// de-duplication ledger anywhere in this project.
				rowId: e.id,
				data: { userId, at: e.at, dev: e.dev, kind: e.kind, payload: payloadOf(e) },
				// Read only. No update, no delete — the log is immutable because the
				// server refuses to mutate it.
				permissions: [this.sdk.Permission.read(this.sdk.Role.user(userId))],
			});
		} catch (err) {
			// 409 means this exact row already exists, which means the push we are
			// retrying actually landed. That is success.
			if (codeOf(err) === 409) return;
			throw kind(err);
		}
	}

	async putSnapshot(userId: string, s: RemoteSnapshot): Promise<void> {
		const data = {
			version: s.version,
			content: s.content,
			coversUpTo: s.coversUpTo,
			state: JSON.stringify(s.state),
		};
		try {
			await this.sdk.tables.upsertRow({
				databaseId: this.sdk.databaseId,
				tableId: this.sdk.snapshotsTable,
				rowId: userId,
				data,
				permissions: [
					this.sdk.Permission.read(this.sdk.Role.user(userId)),
					this.sdk.Permission.update(this.sdk.Role.user(userId)),
				],
			});
		} catch (e) {
			throw kind(e);
		}
	}
}
